import * as React from "react"
import { useLocation, useRouteError } from "react-router-dom"
import { toast } from "sonner"

import { AppProviders } from "@/app/runtime-providers"
import { useTranslations } from "@/i18n"
import { posthog } from "@/instrument"
import { isRecoverableLazyImportError } from "@/app/router/lazy-import-recovery"
import { AppErrorScreen, type AppErrorScreenLabels } from "@ibada/ui/components/app-error-screen"

/**
 * react-router `errorElement` for the tenant app. Renders the **single**
 * canonical `AppErrorScreen` (from `@ibada/ui`) — same component used by
 * the tenant `AppErrorBoundary` and the admin route error route.
 *
 * Do not fork the layout here. Localised labels and recovery callbacks
 * are the only per-app surface.
 */
function getRouteError(error: unknown): Error {
  if (error instanceof Error) return error
  return new Error(String(error ?? ""))
}

function TenantRouteErrorContent() {
  const error = useRouteError()
  const location = useLocation()
  const t = useTranslations("errorBoundary")
  const isStaleChunkError = isRecoverableLazyImportError(error)
  const routeError = React.useMemo(() => getRouteError(error), [error])

  React.useEffect(() => {
    posthog?.captureException(routeError, {
      extra: {
        pathname: location.pathname,
        source: "tenant_route_error",
      },
    })
  }, [location.pathname, routeError])

  const labels: AppErrorScreenLabels = {
    eyebrow: t("eyebrow"),
    title: isStaleChunkError ? t("staleChunkTitle") : t("title"),
    description: isStaleChunkError ? t("staleChunkDescription") : t("description"),
    reloadLabel: t("actions.reload"),
    homeLabel: t("actions.home"),
    detailsLabel: t("details"),
    routeLabel: t("route"),
    messageLabel: t("message"),
    copyLabel: t("actions.copy"),
    copiedLabel: t("actions.copied"),
  }

  const copySuccessLabel = t("copySuccess")
  const copyErrorLabel = t("copyError")

  const handleReload = React.useCallback(() => {
    window.location.reload()
  }, [])

  const handleHome = React.useCallback(() => {
    window.location.assign("/dashboard")
  }, [])

  return (
    <AppErrorScreen
      error={routeError}
      pathname={location.pathname}
      labels={labels}
      showStackTrace={import.meta.env.DEV}
      onReload={handleReload}
      onHome={handleHome}
      onCopySuccess={() => toast.success(copySuccessLabel)}
      onCopyError={() => toast.error(copyErrorLabel)}
    />
  )
}

export function RootProvidersErrorRoute() {
  return (
    <AppProviders>
      <TenantRouteErrorContent />
    </AppProviders>
  )
}
